import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

/**
 * Customer-side WebSocket facade. Single-socket (newSocket) variant of the
 * vendor app's WebsocketService — see that file for the full rationale.
 *
 * The two material differences from the previous implementation:
 *
 *  1. Multi-subscriber support via Subjects. The old version assigned to
 *     `socket.onmessage` per-subscribe, which silently replaced the previous
 *     handler. Right now AppComponent and MyOrders / OrderDetail each set up
 *     their own subscription — so before this fix, only the most recent
 *     component to subscribe was actually receiving messages, depending on
 *     route order.
 *
 *  2. {@link #onServerDown} no longer NPEs. It used to reference
 *     {@code this.socket} (never initialised on this customer service —
 *     only {@code newSocket} is) so the call would throw. Reworked to
 *     reload the page on close of the actual newSocket, which matches the
 *     original commented intent.
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService {

  private newSocket: WebSocket | null = null;
  readonly socket_uri: string = environment.SOCKET_URL;

  private readonly open$ = new Subject<Event>();
  private readonly message$ = new Subject<MessageEvent>();
  // Drives the {@link #onServerDown} reload — emitted once on the first
  // unexpected close. We don't surface it as a public observable today;
  // existing consumers only want the side-effect.
  private serverDownArmed = false;

  constructor(
    private router: Router
  ) {}

  /**
   * Lazy connect. The socket isn't opened until the first subscriber attaches
   * — avoids racing critical-path API calls during a cold boot. Idempotent
   * so multiple subscribers all land on the same underlying connection.
   */
  private ensureSocket(): void {
    if (this.newSocket) return;
    this.newSocket = new WebSocket(this.socket_uri);
    this.newSocket.addEventListener('open',   (e) => this.open$.next(e));
    this.newSocket.addEventListener('message', (e) => this.message$.next(e as MessageEvent));
    this.newSocket.addEventListener('close',  () => {
      if (this.serverDownArmed) {
        // Same behaviour as the original (commented) onServerDown — reload
        // so the app reconnects cleanly. Guarded by serverDownArmed so a
        // close that fires before anything armed the reload doesn't take
        // the page out from under a customer mid-checkout.
        window.location.reload();
      }
    });
  }

  /**
   * Emits the open Event when the socket connects. Subscribers historically
   * ignore the value — the signal alone is what mattered. Returning Event
   * here keeps the contract flexible without breaking any caller.
   */
  listenNewSocket(): Observable<Event> {
    this.ensureSocket();
    return this.open$.asObservable();
  }

  /**
   * Inbound messages. Callers read {@code event.data}; the MessageEvent
   * shape is identical to the original implementation.
   */
  emitNewSocketMessage(): Observable<MessageEvent> {
    this.ensureSocket();
    return this.message$.asObservable();
  }

  /**
   * Arms the page-reload-on-close behaviour. Idempotent and safe to call
   * multiple times. Will also open the socket if it hasn't been opened yet,
   * so {@code onServerDown} can be invoked from a bootstrap path without
   * depending on a subscribe call having run first.
   */
  onServerDown(): void {
    this.ensureSocket();
    this.serverDownArmed = true;
  }

  /**
   * Legacy stub. Consumers should use {@code takeUntil(destroy$)} on their
   * subscriptions; with the Subject-based fan-out there's no per-subscriber
   * handler to remove. Kept to avoid breaking any call site that still
   * invokes this. Previous body tried {@code this.socket.removeAllListeners}
   * which (a) referenced a never-initialised field and (b) called a method
   * the native WebSocket doesn't have.
   */
  stopListening(..._args: Array<string>) {
    // intentionally empty
  }
}
